import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
} from '@material-ui/core';
import { SearchedParticipant } from '../../../store/api/search';
import styles from '../../TargetGroupView/TargetGroupTable/TargetGroupTable.module.css';
import { Skeleton } from '@material-ui/lab';
import CandidateInfoModal from '../../TargetGroupView/TargetGroupTable/CandidateInfoModal/CandidateInfoModal';
import { ReactComponent as ThreeDotsIcon } from '../../../assets/Icons/3dots.svg';
import { ReactComponent as ActivePDFIcon } from '../../../assets/Icons/active-pdf.svg';
import { ReactComponent as DisabledPDFIcon } from '../../../assets/Icons/disabled-pdf.svg';
import { ChevronDownIcon, ChevronUpIcon } from 'evergreen-ui';
import Collapse from '@material-ui/core/Collapse';
import CandidateActivityRow from '../../TargetGroupView/TargetGroupTable/CandidateActivityRow';
import { LightTooltip } from '../../TargetGroupView/TargetGroupTable/TargetGroupTable';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { formatDate } from '../../../utils/time';
import { Link } from 'react-router-dom';
import ProgressSpinner from '../../../components/ProgressSpinner/ProgressSpinner';

interface SearchTableProps {
    participants: SearchedParticipant[];
    selectedIds: Set<string>;
    onSelect: (user_id: string, group_id: string, event: any) => void;
    onSelectAll: () => void;
    isSearchLoading: boolean;
    isPageLoaded: boolean;
    isBatchProcessLoading: boolean;
    isParticipantDataLoading: boolean;
    isSuperAdmin: boolean;
    participantHasMultipleCompanies: boolean;
    usersShowingInfo: string[];
    onUserShowInfoChange: (userIdGroupIdKey: string) => void;
}

const SearchTable: React.FC<SearchTableProps> = ({
    onSelect,
    onSelectAll,
    participants,
    selectedIds,
    isBatchProcessLoading,
    isParticipantDataLoading,
    isSuperAdmin,
    usersShowingInfo,
    onUserShowInfoChange,
    participantHasMultipleCompanies,
    isSearchLoading,
    isPageLoaded,
}) => {
    const searchTableTheme = useMemo(
        () =>
            createTheme({
                overrides: {
                    MuiTableHead: {
                        root: {
                            border: 'solid 1px #bbc9d9',
                        },
                    },
                    MuiTableRow: {
                        root: {
                            '&$selected, &$selected:hover': {
                                backgroundColor: '#F6F9FD',
                            },
                        },
                        head: { border: 'solid 1px #bbc9d9' },
                    },
                    MuiTableCell: {
                        root: {
                            '&$head': {
                                fontWeight: '700',
                                color: '#445870',
                                backgroundColor: '#F4F6FA',
                                borderRadius: 0,
                            },
                            lineHeight: '0.7',
                            verticalAlign: 'middle',
                        },
                        sizeSmall: { padding: '5px' },
                    },
                    MuiTableContainer: {
                        root: {
                            maxHeight: 'calc(100vh - 480px)',
                        },
                    },
                },
            }),
        [],
    );

    const [activeModalKey, setActiveModalKey] = useState<string | null>(null);

    useEffect(() => {
        document.body.style.overflow = activeModalKey ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [activeModalKey]);

    const totalColumns = useMemo(() => {
        let count = 10;
        if (participantHasMultipleCompanies || isSuperAdmin) count += 1;
        return count;
    }, [participantHasMultipleCompanies, isSuperAdmin]);

    const handleChevronClick = useCallback(
        (uniqueKey: string) => {
            onUserShowInfoChange(uniqueKey);
        },
        [onUserShowInfoChange],
    );

    const handleCheckboxChange = useCallback(
        (userId: string, groupId: string, event: any) => {
            onSelect(userId, groupId, event);
        },
        [onSelect],
    );

    const handleModalOpen = useCallback((uniqueKey: string) => {
        setActiveModalKey(uniqueKey);
    }, []);

    const selectedCount = selectedIds.size;

    return (
        <ThemeProvider theme={searchTableTheme}>
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={1} />
                            <TableCell padding="checkbox">
                                <Checkbox
                                    disabled={isBatchProcessLoading}
                                    indeterminate={
                                        selectedCount > 0 &&
                                        selectedCount < participants.length
                                    }
                                    checked={
                                        participants.length > 0 &&
                                        participants.length === selectedCount
                                    }
                                    onChange={onSelectAll}
                                    classes={{
                                        checked: styles.checkedBox,
                                        indeterminate: styles.checkedBox,
                                    }}
                                    color="primary"
                                />
                            </TableCell>
                            <TableCell>First name</TableCell>
                            <TableCell>Last name</TableCell>
                            <TableCell>E-mail</TableCell>
                            {(participantHasMultipleCompanies ||
                                isSuperAdmin) && <TableCell>Company</TableCell>}
                            <TableCell>Group</TableCell>
                            <TableCell>Registered</TableCell>
                            <TableCell>Report</TableCell>
                            <TableCell align="center">
                                Report generation date
                            </TableCell>
                            <TableCell align="right">More info</TableCell>
                        </TableRow>
                    </TableHead>
                    {isSearchLoading && (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    colSpan={totalColumns}
                                    align="center">
                                    <ProgressSpinner />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )}
                    {!isSearchLoading && participants.length > 0 && (
                        <TableBody>
                            {participants.map((participant) => {
                                const uniqueKey = `${participant.user_id}||${participant.group_id}`;
                                const isSelected = selectedIds.has(uniqueKey);
                                const showInfo =
                                    usersShowingInfo.includes(uniqueKey);

                                return (
                                    <React.Fragment key={uniqueKey}>
                                        <TableRow hover selected={isSelected}>
                                            <TableCell padding="checkbox">
                                                {showInfo ? (
                                                    <ChevronUpIcon
                                                        size={25}
                                                        onClick={() =>
                                                            onUserShowInfoChange(
                                                                uniqueKey,
                                                            )
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                ) : (
                                                    <ChevronDownIcon
                                                        size={25}
                                                        onClick={() =>
                                                            handleChevronClick(
                                                                uniqueKey,
                                                            )
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                )}{' '}
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    disabled={
                                                        isBatchProcessLoading
                                                    }
                                                    checked={isSelected}
                                                    onChange={(event) =>
                                                        handleCheckboxChange(
                                                            participant.user_id,
                                                            participant.group_id,
                                                            event,
                                                        )
                                                    }
                                                    color="primary"
                                                    classes={{
                                                        checked:
                                                            styles.checkedBox,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {participant.first_name}
                                            </TableCell>
                                            <TableCell>
                                                {participant.last_name}
                                            </TableCell>
                                            <TableCell>
                                                {participant.email}
                                            </TableCell>
                                            {isParticipantDataLoading ? (
                                                <>
                                                    <TableCell align="left">
                                                        <Skeleton
                                                            variant="text"
                                                            width="100%"
                                                            height={30}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        <Skeleton
                                                            variant="text"
                                                            width="100%"
                                                            height={30}
                                                        />
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    {(participantHasMultipleCompanies ||
                                                        isSuperAdmin) && (
                                                        <TableCell align="left">
                                                            <Link
                                                                className="link"
                                                                to={`/${participant.company_slug}`}>
                                                                {
                                                                    participant.company_name
                                                                }
                                                            </Link>
                                                        </TableCell>
                                                    )}
                                                    <TableCell align="left">
                                                        <Link
                                                            className="link"
                                                            to={`/${participant.company_slug}/groups/${participant.group_id}`}>
                                                            {
                                                                participant.group_name
                                                            }
                                                        </Link>
                                                    </TableCell>
                                                </>
                                            )}
                                            <TableCell>
                                                {formatDate(
                                                    participant.invited_at,
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {!!participant.report_generated_at ? (
                                                    <LightTooltip
                                                        title={`Report generated on ${formatDate(
                                                            participant.report_generated_at,
                                                        )}`}
                                                        placement="left">
                                                        <Link
                                                            to={`/${participant.company_slug}/groups/${participant.group_id}/results/${participant.user_id}`}
                                                            target="_blank">
                                                            <ActivePDFIcon />
                                                        </Link>
                                                    </LightTooltip>
                                                ) : (
                                                    <div>
                                                        <DisabledPDFIcon />
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {participant.report_generated_at
                                                    ? formatDate(
                                                          participant.report_generated_at,
                                                      )
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <div
                                                    className={
                                                        styles.info_modal
                                                    }
                                                    onClick={() =>
                                                        handleModalOpen(
                                                            uniqueKey,
                                                        )
                                                    }>
                                                    <ThreeDotsIcon />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={totalColumns}
                                                style={{
                                                    padding: 0,
                                                    background: '#e7f8f8',
                                                }}>
                                                <Collapse
                                                    in={showInfo}
                                                    timeout="auto">
                                                    <CandidateActivityRow
                                                        groupHasOptOutQuestionnaire={
                                                            participant.has_opt_out_questionnaire
                                                        }
                                                        isSuperAdmin={
                                                            isSuperAdmin
                                                        }
                                                        companyId={
                                                            participant.company_id
                                                        }
                                                        groupId={
                                                            participant.group_id
                                                        }
                                                        participant={
                                                            participant
                                                        }
                                                        invitationId={
                                                            participant.invitation_id
                                                        }
                                                        scanTemplateId={
                                                            participant.scan_template_id
                                                        }
                                                        showInfo={showInfo}
                                                    />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>

                                        {activeModalKey === uniqueKey && (
                                            <CandidateInfoModal
                                                companyId={
                                                    participant.company_id
                                                }
                                                groupId={participant.group_id}
                                                participant={participant}
                                                isSuperAdmin={isSuperAdmin}
                                                invitationId={
                                                    participant.invitation_id
                                                }
                                                closeCandidateDetailsModal={() =>
                                                    setActiveModalKey(null)
                                                }
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    )}
                    {!isSearchLoading &&
                        isPageLoaded &&
                        participants.length === 0 && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        colSpan={totalColumns}
                                        align="center"
                                        className="no-result">
                                        <h3>No results found</h3>
                                        <p>
                                            There weren’t any candidates found
                                            that match your search.
                                        </p>
                                        <p>
                                            Manually adjust your search query or
                                            clear all filters.
                                        </p>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                </Table>
            </TableContainer>
        </ThemeProvider>
    );
};

export default SearchTable;
